import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, CheckCircle2, ArrowLeft } from 'lucide-react';

const LOGO_URL = "/images/area-logo.png";

export default function HireMoxey() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="hire-moxey-page">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100/80" data-testid="hire-nav">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="hire-nav-logo">
            <img src={LOGO_URL} alt="AREA" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/moxey">
              <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full px-5 font-body" data-testid="hire-nav-learn">
                Learn More
              </Button>
            </Link>
            <Link to="/app">
              <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-full px-6 text-sm font-semibold shadow-sm font-body" data-testid="hire-nav-app">
                Open App
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="py-16 lg:py-24 px-6">
        <div className="max-w-lg mx-auto">

          {!submitted ? (
            <>
              <div className="text-center mb-10">
                <div className="w-14 h-14 rounded-2xl bg-[#2563eb] flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="hire-title">
                  Hire Moxey
                </h1>
                <p className="text-gray-500 mt-3 text-base font-body leading-relaxed max-w-sm mx-auto">
                  Tell us about your business and Moxey will be ready to help.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)] border border-gray-100/60 p-8"
                data-testid="hire-form"
              >
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-body">Name</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full h-11 px-4 rounded-xl bg-[#f8f9fb] border border-gray-200 text-sm font-body text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]/40 transition-all"
                      placeholder="Your name"
                      data-testid="hire-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-body">Email</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full h-11 px-4 rounded-xl bg-[#f8f9fb] border border-gray-200 text-sm font-body text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]/40 transition-all"
                      placeholder="you@company.com"
                      data-testid="hire-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-body">Company</label>
                    <input
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full h-11 px-4 rounded-xl bg-[#f8f9fb] border border-gray-200 text-sm font-body text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]/40 transition-all"
                      placeholder="Your company (optional)"
                      data-testid="hire-company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-body">What do you need help with?</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-[#f8f9fb] border border-gray-200 text-sm font-body text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]/40 transition-all resize-none"
                      placeholder="Tell us about your business goals..."
                      data-testid="hire-message"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full mt-6 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl py-6 text-base font-bold shadow-lg shadow-blue-500/20"
                  data-testid="hire-submit"
                >
                  Hire Moxey
                  <img src="/images/moxey-avatar.png" alt="Moxey" className="ml-2 w-5 h-5 rounded-full object-cover" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center bg-white rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)] border border-gray-100/60 p-10" data-testid="hire-success">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">You're all set</h2>
              <p className="text-gray-500 text-base font-body leading-relaxed max-w-sm mx-auto mb-8">
                Moxey is getting ready to help run your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/app">
                  <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl px-8 py-5 text-sm font-bold shadow-lg shadow-blue-500/20" data-testid="hire-open-app">
                    Open Moxey
                    <Sparkles className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="rounded-2xl px-8 py-5 text-sm font-semibold text-gray-700 border-gray-200" data-testid="hire-home">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white py-8 px-6 mt-auto">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AREA" className="h-7 w-auto opacity-60" />
            <span className="text-gray-400 text-xs font-body">American Real Estate Alliance</span>
          </div>
          <p className="text-gray-400 text-xs font-body">&copy; {new Date().getFullYear()} AREA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
